import { useCallback } from "react";
import { useAppDispatch, useAppSelector, resetApplicationState } from "@/app/store";
import { selectCognitoConfig } from "@/features/app-config/model/tenantConfigurationSlice";
import { clearPrefetchedAppItemsStreams } from "@/features/published-apps/lib/publishedAppItemsPrefetch";
import { clearPublishedAppViewCache } from "@/features/published-apps/lib/publishedAppViewCache";
import { resolveAuthRealm } from "@/shared/auth/authRealm";
import { useAuth } from "@/shared/auth/useAuth";
import {
  buildCognitoLogoutUrl,
  clearAuthSession,
  readStoredTokens,
  revokeCognitoRefreshToken,
} from "@/shared/auth/cognito";
import { markPostLogoutSession } from "@/shared/auth/postLogoutSession";

export function useCognitoSignOut() {
  const dispatch = useAppDispatch();
  const { logout } = useAuth();
  const cognitoConfig = useAppSelector(selectCognitoConfig);
  const authRealm = resolveAuthRealm();

  return useCallback(() => {
    const stored = readStoredTokens(authRealm);
    if (cognitoConfig && stored) {
      // Best-effort and fire-and-forget: revocation must not delay the redirect below.
      void revokeCognitoRefreshToken({
        cognitoDomain: cognitoConfig.cognitoDomain,
        clientId: cognitoConfig.clientId,
        refreshToken: stored.refreshToken,
      }).catch(() => {
        // Local cookies are cleared regardless of whether the revoke call succeeded.
      });
    }

    markPostLogoutSession();
    clearAuthSession();
    // Prefetched app data lives outside the store, so `resetApplicationState` does not reach it and
    // the next viewer on this browser would otherwise inherit the last one's favourites.
    clearPrefetchedAppItemsStreams();
    clearPublishedAppViewCache();

    if (cognitoConfig) {
      // Navigate before logout()/dispatch so RequireAuth cannot start a login redirect first.
      window.location.assign(
        buildCognitoLogoutUrl({
          cognitoDomain: cognitoConfig.cognitoDomain,
          clientId: cognitoConfig.clientId,
          // Trailing slash: must exactly match the registered sign-out URL.
          logoutUri: `${window.location.origin}/`,
        }),
      );
      return;
    }

    logout();
    dispatch(resetApplicationState());
  }, [dispatch, logout, cognitoConfig, authRealm]);
}
